import type {
  SegmentCondition,
  ConditionClause,
  SingleCondition,
  ConditionCriterion,
  MultiCondition
} from '../bridge/segments/types';
import {
  isMultiOperator,
  isConditionsGroup,
} from '../bridge/segments/helpers';

import { getCountryName, getFullLangName } from './mapping/langCountryMapping';

const operatorsMap: Record<string, string> = {
  eq: '=',
  noteq: '\u2260',
  notin: 'not in',
  lte: '\u2264',
  gte: '\u2265',
};

function capitalize(str: string = ''): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getOperator(operator: string) {
  return operatorsMap[operator] || operator;
}

function formatValue(tagName: string, value: string = ''): string {
  if (tagName === 'Country') {
    return getCountryName(value);
  }

  if (tagName === 'City') {
    const [countryCode, city] = value.split(', ', 2);
    return `${capitalize(city)} (${getCountryName(countryCode)})`;
  }

  if (tagName === 'Language') {
    return getFullLangName(value);
  }

  return `${value}`;
}

function convertConditionClause(
  clause: ConditionClause,
  formatValue: (value: string) => string = (value) => value,
): string {
  let result;
  if (clause.operator === 'between') {
    result = `${formatValue(clause.values[0])} and ${formatValue(clause.values[1])}`;
  } else if (isMultiOperator(clause.operator)) {
    result = (clause as MultiCondition).values
      .map(formatValue)
      .join(', ');
  } else {
    result = formatValue((clause as SingleCondition).value);
  }
  return `${getOperator(clause.operator)} ${result}`;
}

function convertConditionCriterion(
  conditionCriterion: ConditionCriterion,
  formatValue: (value: string) => string = (value) => value,
): string {
  if (conditionCriterion.valueType === 'date') {
    const { variant } = conditionCriterion.condition;
    if (variant === 'datetime') {
      return convertConditionClause(conditionCriterion.condition.conditionClause);
    }
    if (variant === 'daysAgo') {
      return `${convertConditionClause(conditionCriterion.condition.conditionClause)} days ago`;
    }
    if (variant === 'dateMatch') {
      const { month, day, addDays } = conditionCriterion.condition.value;
      const addDaysStr = addDays > 0 ? `+${addDays}` : '';
      return `match month ${month > 0 ? month : 'now'} day ${day > 0 ? month : 'now'}${addDaysStr}`;
    }
  }
  return convertConditionClause(
    conditionCriterion as ConditionClause,
    formatValue,
  );
}


export function humanizeCondition(
  condition: SegmentCondition,
  brackets?: boolean,
): string {
  if (isConditionsGroup(condition)) {
    const { conditions, operator } = condition;

    const groupString = conditions
      .map((condition) => humanizeCondition(condition, true))
      .join(` ${operator.toUpperCase()} `);

    return (brackets ? `(${groupString})` : groupString);
  }

  if (condition.type === 'tag') {
    const { name, conditionCriterion } = condition;
    const tagText = convertConditionCriterion(conditionCriterion, (value) => formatValue(name, value));
    return `#${name} (${tagText})`;
  }

  if (condition.type === 'event') {
    const { name, count, lastDays, attributes } = condition.event;
    const attributesString = attributes.map((attribute) => {
      return `${attribute.name} (${convertConditionCriterion(attribute.conditionCriterion)})`;
    }).join(' and ');
    return `@${name} (${getOperator(count.operator)} ${count.value} time${count.value > 1 ? 's' : ''} in the last ${lastDays} days${attributesString ? ` with ${attributesString}` : ''})`;
  }

  return 'unknown';
}
